import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AppService } from '@app/app.service';
import { FuiDestroyService } from '@services/destroy.service';
import * as _moment from 'moment';
import { takeUntil } from 'rxjs';
import { FieldConfig, ITrailingButtonEvent } from '../field.interface';
import { DayMonthHeaderComponent } from './day-month-header/day-month-header.component';

const moment = _moment;
export const MY_FORMATS = {
    parse: {
        dateInput: 'DD.MM',
    },
    display: {
        dateInput: 'DD.MM',
        monthYearLabel: 'MMM',
        dateA11yLabel: 'LL',
    },
};

@Component({
    selector: 'app-day-month',
    templateUrl: './day-month.component.html',
    styleUrls: ['../input/input.scss', '../date/date.component.scss', './day-month.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ],
})
export class DayMonthComponent implements OnInit {
    field: FieldConfig;
    group: FormGroup;
    max: string;
    min: string;
    exampleHeader = DayMonthHeaderComponent;

    private readonly destroy = inject(FuiDestroyService);
    private readonly cdr = inject(ChangeDetectorRef);

    constructor(public appService: AppService) {}

    /** Событие TrailingButton */
    outputEvent: EventEmitter<ITrailingButtonEvent> = null;

    ngOnInit() {
        if (this.field.disabled) {
            setTimeout(() => {
                this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }

        this.group
            ?.get(this.field.name)
            ?.statusChanges.pipe(takeUntil(this.destroy))
            .subscribe(() => this.cdr.markForCheck());
    }

    /**
     * Изменение даты
     */
    changeEvent() {
        let ctrlValue = this.group?.get(this.field.name)?.value ? this.group.get(this.field.name).value : null;

        if (typeof ctrlValue === 'string') {
            ctrlValue = moment(ctrlValue, 'DD.MM.YYYY')
                .hour(12)
                .minute(0)
                .second(0)
                .add(moment().utcOffset(), 'm')
                .utc(false);
        }
        if (ctrlValue) {
            this.group?.get(this.field.name)?.setValue(
                moment({
                    year: ctrlValue.year(),
                    month: ctrlValue.month(),
                    date: ctrlValue.date(),
                })
                    .add(12, 'hours')
                    .add(moment().utcOffset(), 'm')
                    .utc(false),
            );
        } else {
            this.group?.get(this.field.name)?.setValue(null);
        }
        if (this.group.get('from') && this.group.get('to')) {
            this.checkValues(this.field.name);
        }
    }

    /**
     * Проверка на равенство дат
     * @param type тип изменяемого поля
     */
    checkValues(type: string) {
        this.min = this.group.get('from')?.value ?? null;
        this.max = this.group.get('to')?.value ?? null;
        const anotherType = type === 'from' ? 'to' : 'from';

        if (this.min && this.max) {
            const min = moment(this.min).format('DD-MM');
            const max = moment(this.max).format('DD-MM');

            // Если значения равны
            if (min === max) {
                this.group.get(anotherType).setErrors(null);
                this.group.get(type).setErrors({ incorrect: true });
            } else {
                this.resetError(type, anotherType);
            }
            // Если одно из полей пустое
        } else {
            this.resetError(type, anotherType);
        }
    }

    /**
     * Сброс ошибки
     * @param type тип изменяемого поля
     * @param anotherType второе изменяемое поля
     */
    resetError(type: string, anotherType: string) {
        this.group.get(type).setErrors(null);
        this.group.get(anotherType).setErrors(null);
        this.group.get('from').setValue(this.min);
        this.group.get('to').setValue(this.max);
    }

    /**
     * Изменить доступность редактирования значения компоненты
     * @param disabled Компонента доступна или недоступна для редактирования?
     */
    setDisabled(disabled: boolean) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }
}
